import AppFrame from "./AppFrame";
import Cookies from "js-cookie";
import { ReactNode, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { EntryPage, EntryPageBackground, EntryPageFooterMode, EntryPageWidth } from "@medentic/polaris-react";
import LocaleSwitcher from "./LocaleSwitcher";
import { PWAInstall } from "./PWAInstall";
import { MobileIcon } from "@medentic/polaris-icons";
import { ROOTS, ROUTES } from "@app/config/constants";
import { URLLinkBuilder, getLocalStorage } from "@shared/lib";
import { usePWAInstall } from "@shared/hooks/navigation/usePWAInstall";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BlockStack,
  Box,
  Button,
  InlineStack,
  Link,
  Text,
} from "@medentic/polaris-react";

interface MedenticAuthEntryPageProps {
  children: ReactNode;
  footer?: ReactNode;
  footerClass?: string;
  wrapperClass?: string;
  showLocaleSwitcher?: boolean;
  showHelpFooter?: boolean;
  background?: EntryPageBackground;
  width?: EntryPageWidth;
  footerMode?: EntryPageFooterMode;
  className?: string;
  contentClassName?: string;
  enableAuthRouteRedirect?: boolean;
}

const WRAPPER_CLASS_TO_WIDTH: Record<string, EntryPageWidth> = {
  "auth-card-wrapper": "sm",
  "white-card-wrapper": "md",
  "questionnaire-card": "lg",
};

function MedenticAuthEntryPage({
  children,
  footer,
  footerClass = "",
  wrapperClass = "",
  showLocaleSwitcher = true,
  showHelpFooter = false,
  background,
  width,
  footerMode,
  className = "",
  contentClassName = "",
  enableAuthRouteRedirect = false,
}: MedenticAuthEntryPageProps) {
  if (import.meta.env.DEV) {
    console.debug("[MedenticAuthEntryPage::MedenticAuthEntryPage]");
  }

  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const navigate = useNavigate();
  const location = useLocation();
  const { isInstallAvailable } = usePWAInstall();
  const [showPWAInstall, setShowPWAInstall] = useState(false);

  const isLoggedIn = !!Cookies.get("access_token");

  useLayoutEffect(() => {
    if (!enableAuthRouteRedirect) {
      return;
    }

    const token = Cookies.get("access_token");
    if (
      token &&
      location.pathname.startsWith(ROOTS.AUTH) &&
      !location.pathname.includes(ROUTES.OTT)
    ) {
      const clinicId = getLocalStorage("__LAST_VISITED_C_ID") as string;
      if (clinicId) {
        navigate(`${ROOTS.CLINIC}/${clinicId}`);
      } else {
        navigate(`${ROOTS.CLINIC}`);
      }
    }
  }, [enableAuthRouteRedirect, location.pathname, navigate]);

  useEffect(() => {
    document.documentElement.classList.add("dark-background");
    return () => document.documentElement.classList.remove("dark-background");
  }, []);

  const termsUrl = URLLinkBuilder.wwwLegal("terms-of-service", locale);
  const privacyUrl = URLLinkBuilder.wwwLegal("privacy", locale);
  const helpUrl = URLLinkBuilder.supportHelpCenter(undefined, locale);

  const resolvedWidth = width ?? WRAPPER_CLASS_TO_WIDTH[wrapperClass];
  const resolvedFooterMode =
    footerMode ?? (footerClass === "relative-auth-footer" ? "inline" : "fixedOnMobile");

  const entryHeader = useMemo(
    () => (
      <InlineStack
        align="space-between"
        blockAlign="center"
        gap={"2000"}
        wrap={false}
      >
        <img
          height={30}
          alt={t("auth.common.logoAlt")}
          src="https://assets.medentic.app/logos/logotype-black.svg"
        />
        {showLocaleSwitcher && <LocaleSwitcher />}
      </InlineStack>
    ),
    [showLocaleSwitcher, t]
  );

  const shouldRenderFooterLinks = !isLoggedIn || showHelpFooter;

  const entryFooter = useMemo(() => {
    if (!footer && !shouldRenderFooterLinks && !isInstallAvailable) {
      return undefined;
    }

    return (
      <BlockStack gap="400">
        {footer}
        {shouldRenderFooterLinks && (
          <InlineStack gap={"400"} align="center">
            <Link url={helpUrl} monochrome removeUnderline>
              <Text as="p" tone="subdued">
                {t("auth.common.footer.help")}
              </Text>
            </Link>
            <Link url={privacyUrl} monochrome removeUnderline>
              <Text as="p" tone="subdued">
                {t("auth.common.footer.privacy")}
              </Text>
            </Link>
            <Link url={termsUrl} monochrome removeUnderline>
              <Text as="p" tone="subdued">
                {t("auth.common.footer.terms")}
              </Text>
            </Link>
          </InlineStack>
        )}
        {isInstallAvailable && (
          <Box paddingBlockStart="400">
            <Button
              variant="secondary"
              onClick={() => setShowPWAInstall(true)}
              size="large"
              fullWidth
              icon={MobileIcon}
            >
              {t("pwa.installApp")}
            </Button>
          </Box>
        )}
      </BlockStack>
    );
  }, [
    footer,
    helpUrl,
    isInstallAvailable,
    privacyUrl,
    shouldRenderFooterLinks,
    t,
    termsUrl,
  ]);

  return (
    <AppFrame>
      <EntryPage
        background={background}
        width={resolvedWidth}
        footerMode={resolvedFooterMode}
        header={entryHeader}
        footer={entryFooter}
        className={[wrapperClass, className].filter(Boolean).join(" ")}
        contentClassName={contentClassName}
      >
        {children}
      </EntryPage>

      <PWAInstall
        isOpen={showPWAInstall}
        onClose={() => setShowPWAInstall(false)}
      />
    </AppFrame>
  );
}

export default MedenticAuthEntryPage;
